/* eslint-disable @typescript-eslint/no-inferrable-types */
/* eslint-disable arrow-body-style */
/* eslint-disable no-nested-ternary */

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import clsx from 'clsx';
import { Player } from '@lottiefiles/react-lottie-player';
import { AnimatePresence, motion } from 'framer-motion';
import {
    IconDigitalWallet,
    IconDownChevron,
    IconEasyRewardsHorizontal,
    IconHelp,
} from 'src/components/Icons';
import { Body, Button, Heading } from 'src/components/UI';
import Tooltip from 'src/components/Tooltip';
import {
    eventMetricsMap,
    useErTooltips,
    useEventsReporter,
    useRouter,
    useStoreData,
    useUserInfo,
} from 'src/hooks';
import { useLoyaltyAccountDetailsQuery } from 'src/services/easyRewardsServices';
import { REWARDS_ENROLLMENT_ROUTES } from 'src/routers/routes';
import { useUserUpdateContext } from 'src/lib/contexts/user-update-context';
import StoreSearchModal from 'src/components/store-search-modal';
import { Store } from 'src/services/types';
import { storeCommunity } from 'src/lib/utils/store-utils';
import StoreDetailsDrawer from 'src/components/store-details-drawer';
import { numberWithCommas } from 'src/lib/utils/app-utils';
import TabsSection from '../modules/TabsSection';
import styles from './styles.module.scss';

interface SecondaryHeaderSectionProps {
    showHowItWorks: boolean;
}
function SecondaryHeaderSection({
    showHowItWorks,
}: SecondaryHeaderSectionProps) {
    const scanRewardsTooltipRef = useRef(null);
    const { showTooltip, setTooltipStatusAsSeen, setShowTooltip } =
        useErTooltips();
    const {
        setPointHistoryDrawer,
        toggleIsUserRewardsDigitalCardDrawerOpen,
        hanRewardsWorks,
    } = useUserUpdateContext();
    const { userInfo } = useUserInfo();
    const { push } = useRouter();
    const { analyticsCustomEvent } = useEventsReporter();

    const onHowItWorksButtonClick = () => {
        if (userInfo && !userInfo.rewardsNumber) {
            push({
                pathname: REWARDS_ENROLLMENT_ROUTES.welcome,
                state: {
                    isNonMigrated: true,
                },
            });
        } else {
            setPointHistoryDrawer(true);
            hanRewardsWorks(true);
        }
        analyticsCustomEvent(eventMetricsMap.easy_rewards, {
            click_text: 'rewards info',
            element_location: 'rewards_navigation',
            event_rewards_number:
                userInfo?.rewardsNumber?.padStart(10, '0') || '',
        });
    };

    const onDigitalWalletButtonClick = () => {
        analyticsCustomEvent(eventMetricsMap.easy_rewards, {
            click_text: 'show my card',
            element_location: 'earn points',
            event_rewards_number:
                userInfo?.rewardsNumber?.padStart(10, '0') || '',
        });
        toggleIsUserRewardsDigitalCardDrawerOpen(true);
        setTooltipStatusAsSeen('scanRewardNumberTooltip');
    };

    return (
        <div className={styles.secondaryHeaderSectionContainer}>
            <Tooltip
                toolTipPlacement="bottom"
                tooltipText="Show your number at checkout to earn points."
                elementRef={scanRewardsTooltipRef}
                onClose={() => {
                    setTooltipStatusAsSeen('scanRewardNumberTooltip');
                }}
                tooltipImgSrc={require('../../../assets/InfoTooltipImages/rewardNumberImg.png')}
                showTooltip={showTooltip('scanRewardNumberTooltip')}
            >
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                <Button
                    ref={scanRewardsTooltipRef}
                    onClick={onDigitalWalletButtonClick}
                    ariaLabel="Scan my Easy Rewards number, Brings up your Easy Rewards number to scan in a store"
                    tabIndex={0}
                    variant="text"
                    contentClassName={styles.horizontalButtonContent}
                >
                    <IconDigitalWallet />
                    <Body size={12} weight="light" color="textWhite">
                        Scan my member number
                    </Body>
                    <IconDownChevron fillColor="fillWhite" />
                </Button>
            </Tooltip>
            {showHowItWorks && (
                <Button
                    onClick={onHowItWorksButtonClick}
                    variant="text"
                    contentClassName={styles.horizontalButtonContent}
                >
                    <IconHelp width={14} height={14} />
                    <Body size={12} weight="light" color="textWhite">
                        How it works
                    </Body>
                </Button>
            )}
        </div>
    );
}

interface ExpandedEasyRewardsHeaderProps {
    scrollToFirstOffer: () => void;
}

function ExpandedEasyRewardsHeader({
    scrollToFirstOffer,
}: ExpandedEasyRewardsHeaderProps) {
    const [elementToShow, setElementToShow] = useState<'points' | 'dollar'>(
        'points'
    );
    const pointsHistoryRef = useRef(null);

    const { setPointHistoryDrawer } = useUserUpdateContext();
    const { showTooltip, setTooltipStatusAsSeen } = useErTooltips();
    const { userInfo } = useUserInfo();
    const { push } = useRouter();
    const { analyticsCustomEvent } = useEventsReporter();

    const { data: loyaltyAccountDetails } = useLoyaltyAccountDetailsQuery({
        rewardsNumber: userInfo?.rewardsNumber,
        config: {
            enabled: !!userInfo?.rewardsNumber,
        },
    });

    const onPointsHistoryButtonClick = () => {
        if (userInfo && !userInfo.rewardsNumber) {
            push({
                pathname: REWARDS_ENROLLMENT_ROUTES.welcome,
                state: {
                    isNonMigrated: true,
                },
            });
        } else {
            setPointHistoryDrawer(true);
        }
        analyticsCustomEvent(eventMetricsMap.easy_rewards, {
            click_text: 'rewards info',
            element_location: 'rewards_navigation',
            event_rewards_number:
                userInfo?.rewardsNumber?.padStart(10, '0') || '',
        });
    };

    const pointBalance =
        loyaltyAccountDetails?.accounts[0]?.walletinfo?.balances?.usable || 0;
    const pointHistory =
        loyaltyAccountDetails?.accounts[0]?.lastearnpoints || 0;
    const dollarValue =
        loyaltyAccountDetails?.accounts[0]?.walletinfo?.balances?.usablevalue ||
        0;

    const MotionBody = useMemo(() => motion(Body), []);

    const BalanceTitle = useCallback(({ children }) => {
        return (
            <MotionBody
                className={styles.balanceTitle}
                size={14}
                color="textWhite"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                {children}
            </MotionBody>
        );
    }, []);
    const BalanceValue = useCallback(({ children }) => {
        return (
            <MotionBody
                className={styles.balanceValue}
                size={36}
                color="textWhite"
                weight="bold"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                {children}
            </MotionBody>
        );
    }, []);
    const BalanceComponent = useCallback(() => {
        return (
            <AnimatePresence>
                {elementToShow === 'points' ? (
                    <>
                        <BalanceTitle>
                            <Player
                                onEvent={(event) => {
                                    if (event === 'complete') {
                                        setElementToShow('dollar');
                                    }
                                }}
                                autoplay
                                src={require('../../../assets/lotties/header_icon_coin_animated.json')}
                                className={styles.coinAnimationPlayer}
                            />
                            Points balance
                        </BalanceTitle>
                        <BalanceValue>
                            {numberWithCommas(pointBalance)}
                        </BalanceValue>
                    </>
                ) : (
                    <>
                        <BalanceTitle>Dollar value</BalanceTitle>
                        <BalanceValue>
                            <sup className={styles.dollarSuperScript}>$</sup>
                            {numberWithCommas(dollarValue)}
                        </BalanceValue>
                    </>
                )}
            </AnimatePresence>
        );
    }, [elementToShow]);

    useEffect(() => {
        if (elementToShow === 'dollar') {
            setTimeout(() => setElementToShow('points'), 4500);
        }
    }, [elementToShow]);

    return (
        <>
            <div className={styles.headerTitleContainer}>
                <IconEasyRewardsHorizontal
                    width={169}
                    height={24}
                    fillColor="fillWhite"
                />
            </div>
            <section className={styles.easyRewardsHeaderContentSection}>
                <BalanceComponent />
                <Body
                    className={styles.historyTitle}
                    size={14}
                    color="textWhite"
                    weight="light"
                >
                    {pointBalance === 0
                        ? 'Start earning today'
                        : 'Points history'}
                </Body>
                <Tooltip
                    toolTipPlacement="bottom"
                    tooltipText="Tap your points to see your points history."
                    onClose={() => {
                        setTooltipStatusAsSeen('pointsHistoryTooltip');
                    }}
                    tooltipImgSrc={require('../../../assets/InfoTooltipImages/pointsHistoryImg.png')}
                    arrowPlacement="middle"
                    elementRef={pointsHistoryRef}
                    showTooltip={showTooltip('pointsHistoryTooltip')}
                    containerClassName={styles.historyValue}
                >
                    <Button
                        ref={pointsHistoryRef}
                        className={styles.historyValue}
                        contentClassName={styles.historyValueContent}
                        variant="text"
                        decoration={
                            pointBalance === 0 ? 'textUnderline' : undefined
                        }
                        fontWeight={pointBalance === 0 ? 'normal' : 'bold'}
                        fontSize={pointBalance === 0 ? 14 : 18}
                        onClick={
                            pointBalance === 0
                                ? scrollToFirstOffer
                                : onPointsHistoryButtonClick
                        }
                    >
                        {pointBalance === 0 ? (
                            'Activate offers'
                        ) : (
                            <>
                                <span
                                    className={
                                        pointHistory === 0
                                            ? styles.historyValueContentWithZero
                                            : pointHistory > 0
                                            ? styles.historyValueContentPositive
                                            : styles.historyValueContentNegative
                                    }
                                >
                                    {numberWithCommas(Math.abs(pointHistory))}
                                </span>
                                <IconDownChevron fillColor="fillWhite" />
                            </>
                        )}
                    </Button>
                </Tooltip>
            </section>
            <SecondaryHeaderSection showHowItWorks />
        </>
    );
}

function CollapsedEasyRewardsHeader() {
    const [elementToShow, setElementToShow] = useState<'points' | 'dollar'>(
        'points'
    );
    const { setPointHistoryDrawer, toggleIsUserRewardsDigitalCardDrawerOpen } =
        useUserUpdateContext();
    const { push } = useRouter();
    const { analyticsCustomEvent } = useEventsReporter();
    const { userInfo } = useUserInfo();
    const { showTooltip, setTooltipStatusAsSeen, setShowTooltip } =
        useErTooltips();
    const { data: loyaltyAccountDetails } = useLoyaltyAccountDetailsQuery({
        rewardsNumber: userInfo?.rewardsNumber,
        config: {
            enabled: !!userInfo?.rewardsNumber,
        },
    });

    const onPointsHistoryButtonClick = () => {
        if (userInfo && !userInfo.rewardsNumber) {
            push({
                pathname: REWARDS_ENROLLMENT_ROUTES.welcome,
                state: {
                    isNonMigrated: true,
                },
            });
        } else {
            setPointHistoryDrawer(true);
        }
        analyticsCustomEvent(eventMetricsMap.easy_rewards, {
            click_text: 'rewards info',
            element_location: 'rewards_navigation',
            event_rewards_number:
                userInfo?.rewardsNumber?.padStart(10, '0') || '',
        });
    };

    const onDigitalWalletButtonClick = () => {
        analyticsCustomEvent(eventMetricsMap.easy_rewards, {
            click_text: 'show my card',
            element_location: 'earn points',
            event_rewards_number:
                userInfo?.rewardsNumber?.padStart(10, '0') || '',
        });
        toggleIsUserRewardsDigitalCardDrawerOpen(true);
        setTooltipStatusAsSeen('scanRewardNumberTooltip');
    };

    const pointBalance =
        loyaltyAccountDetails?.accounts[0]?.walletinfo?.balances?.usable || 0;
    const dollarValue =
        loyaltyAccountDetails?.accounts[0]?.walletinfo?.balances?.usablevalue ||
        0;

    const MotionBody = useMemo(() => motion(Body), []);

    const StyledMotionBody = useCallback(
        ({ children }) => (
            <MotionBody
                className={styles.collapsedHeaderButtonContent}
                size={16}
                color="textWhite"
                weight="bold"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                {children}
            </MotionBody>
        ),
        []
    );

    const ValueToShow = useCallback(() => {
        if (elementToShow === 'dollar') {
            return (
                <StyledMotionBody>
                    ${numberWithCommas(dollarValue)}
                </StyledMotionBody>
            );
        }
        return (
            <StyledMotionBody>
                <Player
                    onEvent={(event) => {
                        if (event === 'complete') {
                            setElementToShow('dollar');
                        }
                    }}
                    autoplay
                    src={require('../../../assets/lotties/header_icon_coin_animated.json')}
                    className={styles.coinAnimationPlayer}
                />
                {numberWithCommas(pointBalance)} pts
            </StyledMotionBody>
        );
    }, [elementToShow, pointBalance, dollarValue]);

    useEffect(() => {
        if (elementToShow === 'dollar') {
            setTimeout(() => setElementToShow('points'), 4500);
        }
    }, [elementToShow]);

    return (
        <>
            <IconEasyRewardsHorizontal
                width={127}
                height={18}
                fillColor="fillWhite"
            />
            <div className={styles.collapsedHeaderButtonsContainer}>
                <button
                    className={styles.collapsedHeaderButton}
                    type="button"
                    onClick={onPointsHistoryButtonClick}
                >
                    <AnimatePresence>
                        <ValueToShow />
                    </AnimatePresence>
                </button>
                <button
                    className={styles.collapsedHeaderButton}
                    type="button"
                    onClick={onDigitalWalletButtonClick}
                >
                    <IconDigitalWallet />
                </button>
            </div>
        </>
    );
}

function ExpandedCouponHeader() {
    const [isStoreChangeDrawerOpen, setIsStoreChangeDrawerOpen] =
        useState(false);
    const [isStoreDetailsDrawerOpen, setIsStoreDetailsDrawerOpen] =
        useState(false);

    const { mainStore, additionalStores, updateMainStore } = useStoreData();
    const onStoreSelected = (store: Store) => {
        updateMainStore(store);
        setIsStoreChangeDrawerOpen(false);
    };

    const openStoreChangeDrawer = () => {
        setIsStoreChangeDrawerOpen(true);
    };

    return (
        <>
            <Heading
                type="h6"
                size={26}
                color="textWhite"
                weight="bold"
                textAlign="textCenter"
            >
                Coupon corner
            </Heading>
            <section className={styles.couponCornerContentSection}>
                <Button
                    fullWidth
                    variant="text"
                    onClick={() => setIsStoreDetailsDrawerOpen(true)}
                >
                    <Body size={14} color="textWhite">
                        Your store
                    </Body>
                    <Body size={20} color="textWhite" weight="bold">
                        {storeCommunity(mainStore) || 'Unknown'}
                    </Body>
                </Button>
                <Button
                    className={styles.historyValue}
                    variant="text"
                    decoration="textUnderline"
                    fontWeight="normal"
                    fontSize={14}
                    onClick={openStoreChangeDrawer}
                >
                    Change
                </Button>
            </section>
            <SecondaryHeaderSection showHowItWorks={false} />
            <StoreSearchModal
                open={isStoreChangeDrawerOpen}
                handleClose={() => setIsStoreChangeDrawerOpen(false)}
                nearbyStores={additionalStores}
                mainStore={mainStore}
                onStoreSelected={onStoreSelected}
            />
            <StoreDetailsDrawer
                mainStore={mainStore}
                nearbyStores={additionalStores}
                onStoreSelected={onStoreSelected}
                open={isStoreDetailsDrawerOpen}
                ignoreIsActiveScreen
                handleClose={() => setIsStoreDetailsDrawerOpen(false)}
            />
        </>
    );
}

function CollapsedCouponHeader() {
    const { mainStore } = useStoreData();
    return (
        <>
            <Heading
                type="h6"
                size={20}
                color="textWhite"
                weight="bold"
                textAlign="textLeft"
            >
                Coupon corner
            </Heading>
            <Body size={16} color="textWhite">
                {storeCommunity(mainStore) || 'Unknown'}
            </Body>
        </>
    );
}

interface HeaderProps {
    containerRef: React.MutableRefObject<HTMLElement | null>;
    activeTabIndex: number;
    scrollToFirstOffer: () => void;
    handleToggleIndex: (index: number, isScrollToTop?: boolean) => void;
}

function Header({
    containerRef,
    activeTabIndex,
    scrollToFirstOffer,
    handleToggleIndex,
}: HeaderProps) {
    const headerRef = useRef<HTMLElement>(null);
    const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
    const [containerTop, setContainerTop] = useState(0);
    const [collapsedHeaderRef, setCollapsedHeaderRef] =
        useState<HTMLElement | null>(null);
    const isEasyRewardsHeader = activeTabIndex === 1;

    const distanceToTop = collapsedHeaderRef?.clientHeight || 0;

    useEffect(() => {
        const element = containerRef?.current;
        if (element) {
            const resizeObserver = new ResizeObserver((entries) => {
                setTimeout(
                    () => setContainerTop(element.getBoundingClientRect().top),
                    100
                );
            });

            resizeObserver.observe(element);

            // Cleanup on unmount
            return () => {
                resizeObserver.unobserve(element);
                resizeObserver.disconnect();
            };
        }
        return undefined;
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const headerBottom =
                headerRef?.current?.getBoundingClientRect().bottom || 0;

            if (headerBottom <= distanceToTop + containerTop) {
                setIsHeaderCollapsed(true);
            } else {
                setIsHeaderCollapsed(false);
            }
        };
        containerRef.current?.addEventListener('scroll', handleScroll);
        return () => {
            containerRef.current?.removeEventListener('scroll', handleScroll);
        };
    }, [containerRef.current, distanceToTop, containerTop]);

    return (
        <>
            <header
                ref={headerRef}
                className={clsx(
                    styles.expandedHeaderContainer,
                    isEasyRewardsHeader
                        ? styles.easyRewardsHeaderBackground
                        : styles.couponHeaderBackground
                )}
            >
                {isEasyRewardsHeader ? (
                    <ExpandedEasyRewardsHeader
                        scrollToFirstOffer={scrollToFirstOffer}
                    />
                ) : (
                    <ExpandedCouponHeader />
                )}
            </header>
            <header
                ref={setCollapsedHeaderRef}
                style={{
                    visibility: isHeaderCollapsed ? 'visible' : 'hidden',
                    top: containerTop,
                }}
                className={clsx(
                    styles.collapsedHeaderContainer,
                    isEasyRewardsHeader
                        ? styles.easyRewardsHeaderBackground
                        : styles.couponHeaderBackground
                )}
            >
                {isEasyRewardsHeader ? (
                    <CollapsedEasyRewardsHeader />
                ) : (
                    <CollapsedCouponHeader />
                )}
            </header>
            <TabsSection
                activeTabIndex={activeTabIndex}
                onToggle={(index) => handleToggleIndex(index, true)}
                distanceToTop={distanceToTop}
                isHeaderCollapsed={isHeaderCollapsed}
            />
        </>
    );
}

export default Header;
